import React, { useState, useEffect } from "react";
import { BiDownload, BiFilter, BiRefresh, BiCalendar } from "react-icons/bi";
import { useAppointmentReport } from "../../../hooks";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  translateStatus,
  getStatusColor,
  formatDisplayDate,
} from "../../../utils/formatters";
import {
  ReportFilters,
  ReportTabs,
  KPICard,
  DataTable,
  ExportModal,
} from "./shared";

const AppointmentsReport = () => {
  const [activeTab, setActiveTab] = useState("stats");
  const [showFilters, setShowFilters] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [filters, setFilters] = useState({
    period: "month",
    startDate: null,
    endDate: null,
    groupBy: "day",
  });

  const { loading, error, getStats, getList, getCancelled, getWaitingList } =
    useAppointmentReport();

  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState({
    data: [],
    pagination: {},
  });
  const [cancelled, setCancelled] = useState({
    data: [],
    pagination: {},
    summary: {},
  });
  const [waitingList, setWaitingList] = useState({ data: [], pagination: {} });

  useEffect(() => {
    loadTabData();
  }, [activeTab, filters]);

  const loadTabData = async () => {
    try {
      switch (activeTab) {
        case "stats":
          const statsData = await getStats(filters);
          setStats(statsData);
          break;
        case "list":
          const listResult = await getList({ ...filters, page: 1 });
          setAppointments(listResult);
          break;
        case "cancelled":
          const cancelledResult = await getCancelled({ ...filters, page: 1 });
          setCancelled(cancelledResult);
          break;
        case "waiting":
          const waitingResult = await getWaitingList({ ...filters, page: 1 });
          setWaitingList(waitingResult);
          break;
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
    }
  };

  const handlePageChange = async (page) => {
    try {
      switch (activeTab) {
        case "list":
          const listResult = await getList({ ...filters, page });
          setAppointments(listResult);
          break;
        case "cancelled":
          const cancelledResult = await getCancelled({ ...filters, page });
          setCancelled(cancelledResult);
          break;
        case "waiting":
          const waitingResult = await getWaitingList({ ...filters, page });
          setWaitingList(waitingResult);
          break;
      }
    } catch (error) {
      console.error("Erreur pagination:", error);
    }
  };

  const handleExport = async (format, options) => {
    // Implémenter l'export
    console.log("Export:", format, options);
  };

  const tabs = [
    { id: "stats", label: "Statistiques" },
    {
      id: "list",
      label: "Liste des RDV",
      count: appointments.pagination?.total,
    },
    { id: "cancelled", label: "Annulés", count: cancelled.pagination?.total },
    {
      id: "waiting",
      label: "Liste d'attente",
      count: waitingList.pagination?.total,
    },
  ];

  const appointmentColumns = [
    {
      key: "code",
      label: "Code",
      render: (row) => <span className="font-mono">{row.code}</span>,
    },
    {
      key: "date",
      label: "Date",
      render: (row) => formatDisplayDate(row.date, "dd/MM/yyyy HH:mm"),
    },
    {
      key: "clientName",
      label: "Client",
      render: (row) => row.clientName || "Anonyme",
    },
    {
      key: "services",
      label: "Services",
      render: (row) => row.services.map((s) => s.name).join(", "),
    },
    {
      key: "employees",
      label: "Employés",
      render: (row) =>
        row.employees.map((e) => e.name).join(", ") || "Non assigné",
    },
    {
      key: "totalAmount",
      label: "Montant",
      render: (row) => formatCurrency(row.totalAmount),
    },
    {
      key: "status",
      label: "Statut",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${getStatusColor(row.status, "appointment")}`}
        >
          {translateStatus(row.status, "appointment")}
        </span>
      ),
    },
  ];

  const cancelledColumns = [
    {
      key: "code",
      label: "Code",
      render: (row) => <span className="font-mono">{row.code}</span>,
    },
    {
      key: "date",
      label: "Date",
      render: (row) => formatDisplayDate(row.date),
    },
    { key: "clientName", label: "Client" },
    { key: "clientPhone", label: "Téléphone" },
    {
      key: "serviceNames",
      label: "Services",
      render: (row) => row.serviceNames.join(", "),
    },
    { key: "employeeName", label: "Employé" },
    { key: "cancellationReason", label: "Raison" },
    {
      key: "status",
      label: "Statut",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${getStatusColor(row.status, "appointment")}`}
        >
          {row.status === "CANCELLED" ? "Annulé" : "Manqué"}
        </span>
      ),
    },
  ];

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        Une erreur est survenue : {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-poppins font-semibold text-[20px] text-title">
          Rapport des rendez-vous
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiFilter size={18} />
            Filtres
          </button>
          <button
            onClick={() => setShowExport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors"
          >
            <BiDownload size={18} />
            Exporter
          </button>
          <button
            onClick={loadTabData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiRefresh size={18} />
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Onglets */}
      <ReportTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Contenu */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
        </div>
      ) : (
        <>
          {activeTab === "stats" && stats && (
            <div className="space-y-6">
              {/* KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                  title="Total RDV"
                  value={formatNumber(stats.overview.totalAppointments)}
                  icon="📅"
                />
                <KPICard
                  title="CA généré"
                  value={formatCurrency(stats.overview.totalRevenue)}
                  icon="💰"
                />
                <KPICard
                  title="Moyenne/jour"
                  value={formatNumber(stats.overview.averagePerDay)}
                  icon="📊"
                />
                <KPICard
                  title="Taux complétion"
                  value={formatPercentage(stats.overview.completionRate)}
                  icon="✅"
                  color="green"
                />
              </div>

              {/* Taux d'annulation */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard
                  title="Taux annulation"
                  value={formatPercentage(stats.overview.cancellationRate)}
                  icon="❌"
                  color="red"
                />
                <KPICard
                  title="Taux absence"
                  value={formatPercentage(stats.overview.noShowRate)}
                  icon="🚫"
                  color="orange"
                />
                <KPICard
                  title="RDV complétés"
                  value={formatNumber(stats.byStatus?.COMPLETED?.count || 0)}
                  icon="✓"
                  color="green"
                />
              </div>

              {/* Répartition par statut */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-poppins font-semibold text-[16px] mb-4">
                  Répartition par statut
                </h3>
                <div className="space-y-3">
                  {Object.entries(stats.byStatus).map(([status, data]) => (
                    <div key={status} className="flex items-center gap-4">
                      <span className="w-32 font-nunito text-[14px]">
                        {translateStatus(status, "appointment")}
                      </span>
                      <div className="flex-1">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              status === "COMPLETED"
                                ? "bg-green-500"
                                : status === "CANCELLED"
                                  ? "bg-red-500"
                                  : status === "PENDING"
                                    ? "bg-yellow-500"
                                    : status === "CONFIRMED"
                                      ? "bg-blue-500"
                                      : "bg-gray-500"
                            }`}
                            style={{ width: `${data.percentage}%` }}
                          />
                        </div>
                      </div>
                      <span className="w-24 font-nunito text-[14px] text-right">
                        {formatNumber(data.count)}
                      </span>
                      <span className="w-16 font-nunito text-[14px] text-gray-500">
                        {formatPercentage(data.percentage)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top services */}
              {stats.byService && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-poppins font-semibold text-[16px] mb-4">
                    Top services
                  </h3>
                  <div className="space-y-3">
                    {stats.byService.slice(0, 5).map((service) => (
                      <div
                        key={service.serviceId}
                        className="flex items-center justify-between"
                      >
                        <span className="font-nunito text-[14px]">
                          {service.serviceName}
                        </span>
                        <div className="flex items-center gap-4">
                          <span className="font-nunito text-[14px]">
                            {formatNumber(service.count)} RDV
                          </span>
                          <span className="font-nunito text-[14px] font-semibold">
                            {formatCurrency(service.revenue)}
                          </span>
                          <span className="font-nunito text-[12px] text-gray-500">
                            {formatPercentage(service.cancellationRate)} annulés
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Période */}
              <div className="text-sm text-gray-500 text-right">
                Période du {formatDisplayDate(stats.period.start)} au{" "}
                {formatDisplayDate(stats.period.end)}
              </div>
            </div>
          )}

          {activeTab === "list" && (
            <DataTable
              columns={appointmentColumns}
              data={appointments.data}
              pagination={appointments.pagination}
              onPageChange={handlePageChange}
            />
          )}

          {activeTab === "cancelled" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <KPICard
                  title="Total annulés"
                  value={formatNumber(cancelled.summary?.totalCancelled || 0)}
                  icon="❌"
                  color="red"
                />
                <KPICard
                  title="Total manqués"
                  value={formatNumber(cancelled.summary?.totalNoShow || 0)}
                  icon="🚫"
                  color="orange"
                />
                <KPICard
                  title="Taux d'annulation"
                  value={formatPercentage(
                    cancelled.summary?.cancellationRate || 0,
                  )}
                  icon="📊"
                />
              </div>
              <DataTable
                columns={cancelledColumns}
                data={cancelled.data}
                pagination={cancelled.pagination}
                onPageChange={handlePageChange}
              />
            </>
          )}

          {activeTab === "waiting" && (
            <DataTable
              columns={[
                { key: "clientName", label: "Client" },
                { key: "clientPhone", label: "Téléphone" },
                {
                  key: "requestedServices",
                  label: "Services demandés",
                  render: (row) => row.requestedServices?.join(", "),
                },
                {
                  key: "preferredDate",
                  label: "Date préférée",
                  render: (row) =>
                    row.preferredDate
                      ? formatDisplayDate(row.preferredDate)
                      : "-",
                },
                {
                  key: "preferredTime",
                  label: "Heure",
                  render: (row) => row.preferredTime || "-",
                },
                {
                  key: "notified",
                  label: "Notifié",
                  render: (row) => (row.notified ? "✅" : "❌"),
                },
                {
                  key: "requestedAt",
                  label: "Demandé le",
                  render: (row) => formatDisplayDate(row.requestedAt),
                },
              ]}
              data={waitingList.data}
              pagination={waitingList.pagination}
              onPageChange={handlePageChange}
              emptyMessage="Aucune demande en liste d'attente"
            />
          )}
        </>
      )}

      {/* Modal d'export */}
      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          onExport={handleExport}
        />
      )}
    </div>
  );
};

export default AppointmentsReport;
